import React, { useCallback, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Pagination,
  InputAdornment,
  Chip,
  Button,
  Tooltip,
  IconButton,
} from '@mui/material'
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Archive as ArchiveIcon,
  Refresh as RefreshIcon,
  VerifiedUser as VerifyIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import {
  useGetAuditLogsQuery,
  useGetAuditStatsQuery,
  useArchiveAuditLogsMutation,
  useLazyExportAuditLogsQuery,
  useLazyVerifyAuditLogIntegrityQuery,
  AuditLogFilters,
  AuditActionType,
  AuditEventStatus,
  AuditEventSeverity,
  AuditLogEntry,
} from '@store/api/adminApi'
import AdminPageGate from '@components/auth/AdminPageGate'
import { ResponsiveTable } from '@components/layout/ResponsiveTable'

const SEVERITY_COLORS: Record<string, 'default' | 'info' | 'warning' | 'error' | 'success'> = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  critical: 'error',
}

const STATUS_COLORS: Record<string, 'default' | 'success' | 'error' | 'warning'> = {
  success: 'success',
  failure: 'error',
  error: 'warning',
}

const AdminAuditLogs: React.FC = () => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [actionType, setActionType] = useState<AuditActionType | ''>('')
  const [severity, setSeverity] = useState<AuditEventSeverity | ''>('')
  const [status, setStatus] = useState<AuditEventStatus | ''>('')
  const [resourceType, setResourceType] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const filters: AuditLogFilters = {
    page,
    limit: 25,
    ...(search && { search }),
    ...(actionType && { actionType }),
    ...(severity && { severity }),
    ...(status && { status }),
    ...(resourceType && { resourceType }),
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
  }

  const { data: logsData, isLoading, error, refetch } = useGetAuditLogsQuery(filters)
  const { data: statsData } = useGetAuditStatsQuery({
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
  })
  const [archiveLogs, { isLoading: archiving }] = useArchiveAuditLogsMutation()
  const [triggerExport, { isFetching: exporting }] = useLazyExportAuditLogsQuery()
  const [triggerVerify, { isFetching: verifying }] = useLazyVerifyAuditLogIntegrityQuery()
  const [exportError, setExportError] = useState<string | null>(null)
  const [verifyResult, setVerifyResult] = useState<{ id: string; valid: boolean } | null>(null)

  const handleExport = async () => {
    setExportError(null)
    try {
      const blob = await triggerExport(filters).unwrap()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setExportError('Export failed. Please try again or narrow your filters.')
    }
  }

  const handleVerify = useCallback(async (logId: string) => {
    try {
      const result = await triggerVerify(logId).unwrap()
      setVerifyResult({ id: logId, valid: result.data?.integrityValid ?? false })
    } catch {
      setVerifyResult({ id: logId, valid: false })
    }
  }, [triggerVerify])

  const handleArchive = async () => {
    if (!window.confirm('Mark all expired audit log entries as archived? Records are retained but flagged.')) return
    await archiveLogs()
  }

  const clearFilters = () => {
    setSearch('')
    setActionType('')
    setSeverity('')
    setStatus('')
    setResourceType('')
    setStartDate('')
    setEndDate('')
    setPage(1)
  }

  const stats = statsData?.data

  const auditColumns = useMemo(
    () => [
      {
        id: 'timestamp',
        header: 'Timestamp',
        renderCell: (log: AuditLogEntry) => (
          <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
            {format(new Date(log.timestamp), 'MMM dd HH:mm:ss')}
          </Typography>
        ),
      },
      {
        id: 'actor',
        header: 'Actor',
        renderCell: (log: AuditLogEntry) => (
          <Box>
            <Typography variant="body2">{log.admin?.email ?? log.adminId ?? '—'}</Typography>
            <Typography variant="caption" color="text.secondary">{log.actorType}</Typography>
          </Box>
        ),
      },
      {
        id: 'action',
        header: 'Action',
        renderCell: (log: AuditLogEntry) => (
          <Box>
            <Typography variant="body2" fontFamily="monospace">{log.action}</Typography>
            <Typography variant="caption" color="text.secondary">{log.actionType}</Typography>
          </Box>
        ),
      },
      {
        id: 'resource',
        header: 'Resource',
        renderCell: (log: AuditLogEntry) => (
          <Box>
            <Typography variant="body2">{log.resource}</Typography>
            {log.resourceId && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {log.resourceId.slice(0, 12)}…
              </Typography>
            )}
          </Box>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        renderCell: (log: AuditLogEntry) => (
          <Chip label={log.status} color={STATUS_COLORS[log.status] ?? 'default'} size="small" />
        ),
      },
      {
        id: 'severity',
        header: 'Severity',
        renderCell: (log: AuditLogEntry) => (
          <Chip
            label={log.severity}
            color={SEVERITY_COLORS[log.severity] ?? 'default'}
            size="small"
            variant={log.severity === 'critical' ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        id: 'ip',
        header: 'IP',
        renderCell: (log: AuditLogEntry) => (
          <Typography variant="caption" color="text.secondary">{log.ipAddress ?? '—'}</Typography>
        ),
      },
      {
        id: 'verify',
        header: 'Verify',
        renderCell: (log: AuditLogEntry) => (
          <Tooltip title="Verify integrity">
            <IconButton size="small" onClick={() => handleVerify(log.id)} disabled={verifying}>
              <VerifyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [verifying, handleVerify],
  )

  return (
    <AdminPageGate permission="system:logs" title="Audit Logs">
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">Audit Logs</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<ArchiveIcon />}
            onClick={handleArchive}
            disabled={archiving}
          >
            Archive Old
          </Button>
        </Stack>
      </Box>

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        CSV export includes up to 10,000 matching records. Use date filters to export large ranges in batches.
      </Alert>

      {verifyResult && (
        <Alert
          severity={verifyResult.valid ? 'success' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setVerifyResult(null)}
        >
          Integrity check for log {verifyResult.id.slice(0, 8)}…: {verifyResult.valid ? 'valid' : 'invalid or tampered'}
        </Alert>
      )}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          {[
            { label: 'Total Events', value: stats.totalEvents },
            { label: 'Failed Auth', value: stats.failedAuthEvents, color: 'error.main' },
            { label: 'Suspicious', value: stats.suspiciousEvents, color: 'warning.main' },
          ].map(({ label, value, color }) => (
            <Grid item xs={12} sm={4} key={label}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography variant="h5" fontWeight={700} color={color ?? 'primary.main'}>
                    {value.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search action, resource, ID…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Action Type</InputLabel>
                <Select value={actionType} label="Action Type" onChange={(e) => setActionType(e.target.value as AuditActionType | '')}>
                  <MenuItem value="">All</MenuItem>
                  {(['CREATE','READ','UPDATE','DELETE','LOGIN','LOGOUT','LOGIN_FAILED','AUTH_FAILURE','CONFIG_CHANGE','BULK_DELETE','PRIVILEGE_ESCALATION','OTHER'] as AuditActionType[]).map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select value={severity} label="Severity" onChange={(e) => setSeverity(e.target.value as AuditEventSeverity | '')}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value as AuditEventStatus | '')}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="failure">Failure</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Resource Type"
                value={resourceType}
                onChange={(e) => setResourceType(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="outlined" onClick={clearFilters} fullWidth>Clear Filters</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">Failed to load audit logs. Please try again.</Alert>
      ) : !logsData?.data?.length ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">No audit log entries found</Typography>
              <Typography variant="body2" color="text.secondary">Try adjusting your filters.</Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          <ResponsiveTable
            rows={logsData.data}
            columns={auditColumns}
            getRowId={(log) => log.id}
            expandTableOnMobile
            data-testid="admin-audit-logs-list"
          />

          {logsData.meta?.pagination && logsData.meta.pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={logsData.meta.pagination.totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
    </AdminPageGate>
  )
}

export default AdminAuditLogs
